/**
 * El borrador del asistente de IA, para retomarlo.
 *
 * Generar ya no gasta una generación: se gasta al crear el proyecto (ver
 * `linkGeneration` en el worker). Pero quien cerraba el asistente sin crear
 * perdía el borrador entero, con sus cambios de colores, letra y textos, y
 * para volver tenía que generar otro. Aquí se guarda —en este navegador, por
 * usuario— y la pantalla de empezar ofrece seguir con él.
 *
 * Lleva el `generationId` dentro del proyecto (`base.aiGeneration`), así que
 * crearlo después lo liga a su generación y la cobra entonces, una vez.
 */
import type { CreateForm } from './preview/compose'

export type SavedAiDraft = {
  savedAt: number
  prompt: string
  form: CreateForm
  step: string
}

/** Pasada una semana ya no se ofrece: el cliente casi seguro empezó otra cosa. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

const keyFor = (userId: string) => `veltica:ai-draft:${userId}`

export function readAiDraft(userId: string | null | undefined): SavedAiDraft | null {
  if (!userId) return null
  try {
    const raw = globalThis.localStorage.getItem(keyFor(userId))
    if (!raw) return null
    const draft = JSON.parse(raw) as SavedAiDraft
    if (!draft?.form || typeof draft.savedAt !== 'number' || Date.now() - draft.savedAt > MAX_AGE_MS) {
      globalThis.localStorage.removeItem(keyFor(userId))
      return null
    }
    return draft
  } catch {
    return null
  }
}

export function saveAiDraft(userId: string | null | undefined, draft: Omit<SavedAiDraft, 'savedAt'>): void {
  if (!userId) return
  try {
    globalThis.localStorage.setItem(keyFor(userId), JSON.stringify({ ...draft, savedAt: Date.now() }))
  } catch {
    // Sin almacenamiento (privado, lleno): el asistente funciona igual, sólo no se puede retomar.
  }
}

export function clearAiDraft(userId: string | null | undefined): void {
  if (!userId) return
  try {
    globalThis.localStorage.removeItem(keyFor(userId))
  } catch {
    // Nada que limpiar si no hay almacenamiento.
  }
}

/** El nombre con que se enseña el borrador: el que escribió el cliente o el de la IA. */
export function aiDraftName(draft: SavedAiDraft): string {
  return draft.form.pageName.trim() || draft.form.aiBusinessName || ''
}
